import { OmniaApiConnection } from "./api/apiConnection"
import { OmniaApiState } from "./api/apiState"
import { HoldingSessions } from "./api/types"
import { OmniaConfig } from "./config"
import { CropYear } from "./cropYear"
import { IFieldsMap, IFieldsMapContainerOptions } from "./fields/map"
import { SharedFieldsService } from "./fields/service"
import { OmniaModuleId, OmniaObjectType, RegionId } from "./generated/enums"
import { HoldingUser, IBusiness, IHolding, IHoldingCropYear } from "./holding"
import { IDatalayersMap } from "./layers/types"
import { ICustomBasemap } from "./map"
import { IMapTopologyService } from "./map/topo/types"
import { ResourceAccessRestrictions } from "./permissions/holding/accessRestrictions"
import { HoldingUserRole } from "./permissions/holding/api"
import { ReadWritePermissions, UserPermissions } from "./permissions/types"
import { IOmniaUser, RoleId } from "./user"

// This pseudo-type is used to tag string literals as "locale" strings,
// thas is, strings that should be translated before being displayed
export type LocaleString = string
export type LocaleStringSet = Record<number | string, string>

export type StringLike = string | { toString(): string }

// Type aliases, used to tag string literals which do not contain translatable text
export type CssString = string
export type SvgString = string
export type WktString = string
export type HtmlTemplateString = string
export type CssClass = string
export type SelectorString = string
export type InternalString = string

// Type alias for date strings, mainly for documentation purpose
export type DateString = string

// Type alias for date numbers, mainly for documentation purpose
export type DateNumber = number

// Type alias for guid strings, mainly for documentation purpose
export type GuidString = `${string}-${string}-${string}-${string}-${string}`

export const EmptyGuidString: GuidString = "00000000-0000-0000-0000-000000000000"

/**
 * Type alias for a color stored in RGB format: an array of 3 numbers (each 0 to 255)
 */
export type RGBColor = number[];

/**
 * Type alias for a color stored in RGBA format: an array of 3 numbers (each 0 to 255) and then 1 fractional opacity number (0-1).
 */
export type RGBAColor = number[];

/**
 * Type alias for a color stored in HSL format: an array of 3 numbers.
 */
export type HSLColor = number[];

export interface INgRegistrationContext {
    service(name: string, serviceConstructor: Function): ng.IModule
    directive(name: string, directiveFactory: ng.IDirectiveFactory): ng.IModule
}

export enum OmniaAppId {
    Omnia = 0,
    Amino = 1,
    Interra = 2
}

export type LoginCredentials =
    | {
        username: string
        password: string
        token?: never
    }
    | {
        username?: never
        password?: never
        token: string
    }

export interface AutoLoginResult {
    success: boolean
    restricted: boolean
    errorMessage?: string
}

export interface LoginResult extends AutoLoginResult {
    token: string
}

export interface OmniaHttpPostOptions {
    clearContentType?: boolean
    changeApiVersion?: boolean
    blockUi?: boolean
}

export interface EventNotification {
    source: OmniaModuleId
    year?: number
    action: string
    type?: OmniaObjectType
    data?: any
}

export interface IOmniaHttpService {

    getApiBaseUrl(): string

    login(credentials: LoginCredentials): ng.IPromise<LoginResult>
    loginAuto(accessToken: string): ng.IPromise<AutoLoginResult>
    logout(): ng.IPromise<any>

    selectHolding(holdingId: number, updateSession: boolean, clientState: any): ng.IPromise<any>

    get<T = any>(url: string, block?: boolean | string, ifModifiedSince?: Date): ng.IPromise<{ data: T }>
    getAndDownload(url: string, fileName: string, contentType: string): ng.IPromise<ng.IHttpPromiseCallbackArg<{}>>
    getExternal<T = any>(url: string, block?: boolean): PromiseLike<T>
    getArrayBuffer(url: string, block?: boolean): ng.IPromise<ArrayBufferLike>

    post<T = any>(url: string, data?: any, changeApiVersion?: boolean): ng.IPromise<{ data: T }>
    post<T = any>(url: string, data?: any, options?: OmniaHttpPostOptions): ng.IPromise<{ data: T }>

    delete<T = any>(url: string, data?: any, changeApiVersion?: boolean): ng.IPromise<{ data: T }>

    put<T = any>(url: string, data?: any, changeApiVersion?: boolean): ng.IPromise<{ data: T }>

    postAndDownload(url: string, fileName: string, postData?: any, contentType?: string): ng.IPromise<any>

    updateSession(update?: { clientState: any }): PromiseLike<any>

    sendNotification(notification: EventNotification): PromiseLike<any>

}

export interface IOmniaMaintenanceService {
    current: IOmniaMaintenance
    next: IOmniaMaintenance
    update(): ng.IPromise<any>
}

export interface IOmniaContext {
    holdingId: number
    yearId: number
}

export interface IOmnniaSessionState {

    /** Login progress (regardless of the holding selection step) */
    state: "loginInProgress" | "loggedIn"

    /**
     * Indicates if "401" responses to calls made to the API should be handled
     * If such a response is received, then the "unauthorized" flag below will be set to true
     * See shared\infrastructure\http.ts and shared\infrastructure\auth.ts for the implementation
     */
    handleUnauthorized?: boolean

    /*
    * Indicates if the session is an unauthorized state
    * When true, the user interface simply displays a messages stating that the session is not valid anymore,
    * and lets the user start a new session be reloading the page
    */
    unauthorized?: boolean

    /**
     * Indicates if the API is unavailable
    * When true, the user interface simply displays a messages stating that the API is not available,
    * and preventing all activity
    */
    unavailable?: boolean

    /**
     * Used to temporarily ignore the http interceptor used by blockUI to start automatically
     */
    disableBlockUI?: boolean

}

export interface IOmniaMaintenance {
    readonly startDate: Date
    readonly endDate: Date
    readonly message: string
}

export interface INewCroppingYear {

    HoldingId: number
    YearId: number

    Cropping: number[]
    DisabledFields: number[]

}

/**
 * Defines the services provided by the global "OmniaSession" object
 */
export interface IOmniaSession extends IOmniaContext, TokenBasedLogin, PasswordReset, SessionHoldings, HoldingInvite {


    readonly isInitialized: boolean
    readonly reloading: boolean
    readonly autoSignup: boolean

    /** Returns the global HTTP service */
    readonly http: IOmniaHttpService

    readonly config: OmniaConfig
    login(credentials: LoginCredentials): ng.IPromise<any>
    logout(): void

    resetContext(): void

    requestFullRefresh(newCroppingYear?: number): void

    readonly sessionState: IOmnniaSessionState

    readonly currentUser: IOmniaUser
    readonly currentUserRole: RoleId
    readonly currentHoldingUserRole: HoldingUserRole
    readonly sessionId: string

    /** True if running in debug mode (this is set at user level, and mainly used for tests) */
    readonly debugMode: boolean

    readonly apiState: OmniaApiState
    readonly apiConnection: OmniaApiConnection

    readonly permissions: UserPermissions
    isReadOnly(selector?: (p: UserPermissions) => ReadWritePermissions): boolean

    readonly accessRestrictions: ResourceAccessRestrictions

    readonly userBusiness: IBusiness

    getUserProperty(name: string): any

    /** Bing Maps API key */
    readonly bingKey: string

    /** Indicates if the app running in production */
    readonly production: boolean

    /** Current holding */
    readonly holding: IHolding

    /** Available years */
    readonly holdingYears: IHoldingCropYear[]

    /** All users linked to this holding (i.e., allowed work on it) */
    readonly holdingUsers: HoldingUser[]

    /** All sessions currently working on this holding (including the current one) */
    readonly holdingSessions: HoldingSessions

    /** Holding region */
    readonly holdingRegion: RegionId

    /** Holding time zone */
    readonly holdingTimeZone: string

    readonly customBasemaps: ICustomBasemap[]

    readonly cropYear: CropYear
    getCroppingYears(): CropYear[]
    openCroppingYear(model: INewCroppingYear): ng.IPromise<boolean>

    readonly lastLoginHolding: IHolding
    readonly lastLoginYear: number

    updateUser(userUpdated: IOmniaUser): void

    /**
     * Local settings (stored in the browser)
     */
    getSettings(namespace: string): IOmniaSettings

    /**
     * Global settings (stored on the back-end)
     */
    readonly globalSettings: {
        get<T = any>(...keys: string[]): T
        set<T = any>(key: string, value: T, userLevel?: boolean): PromiseLike<any>
    }

    /**
     * Sends a notification that the session is still active to the API,
     * and gets updated information about other sessions on the same holding
     */
    updateSession(): PromiseLike<any>

    /**
     * Indicates if there is a valid websocket connection to the API
     */
    hasApiConnection(): boolean

    /**
     * Set the local client state, and sends it to the API, to be broadcast to other sessions
     * @param state Local client state
     */
    setClientSate(state: any): PromiseLike<any>

    isApiRefreshPending(): boolean
    getApiVersion(): number

    /*
    * Disables block-ui while calling a function returning a promise
    */
    disableBlockUI(run: () => PromiseLike<any>): PromiseLike<any>

    /**
     * Returns a seach parameter from the URL
     * Used only in test/dev
     */
    getSeachParam(key: string): string

    /**
     * Terms & conditions
     */
    readonly termsAndConditions: TermsAndConditionsStatus

    /**
     * Accept the current terms & conditions
     */
    acceptTcs(version: string, send: boolean): PromiseLike<boolean>

}


/**
 * Terms and conditions
 */
export type TermsAndConditionsStatus =
    | {
        mustAccept: false
        tcs?: never
    }
    | {
        mustAccept: true
        tcs: {
            version: string
            text: string
        }
    }

/**
 * Used when selecting the current holding and year for the session
 */
interface SessionHoldings {

    /**
     * Selects the current holding and year
     * If this succeeds and the new holding differs from the current one,
     * the application will be reloaded, so that calling this will not return
     */
    selectHolding(holding: IHolding, yearId?: number): ng.IPromise<boolean>

    /**
     * Selects the current year
     */
    selectYear(year: number): boolean

    /**
    * True if the user has a fixed (i.e., closed) holding list
    * This is the case for all users expect admins, which can access any holding
    **/
    readonly fixedHoldingList: boolean

    /**
     * True if "fixedHoldingList" is true, and if this list of holdings has been initialised yet
     */
    hasFixedHoldings(): boolean

    /** Queries for holdings by name or account number */
    queryHoldings(query: string, limit?: number): PromiseLike<IHolding[]>


    /** Gets all available holdings (closed list only) */
    getHoldings(): IHolding[]

    /** Refresh all available holdings */
    refreshHoldings(): ng.IPromise<any>

}


/**
 * Used to track password reset requests at session level
 */
interface PasswordReset {

    readonly passwordResetRequested: boolean

    readonly shouldResetPassword: {
        reset: boolean
        email: string
    }

    setResetPassword(reset: boolean, email: string): void

}

/**
 * The following properties are defined only when using a token to log in
 * This happens either when signing up for a new account, or when requesting a password reset
 */
interface TokenBasedLogin {
    readonly loginLinkToken: string
    readonly loginLinkError: string
    readonly loginLinkUser: string
    readonly restrictedLogin: boolean
}

interface HoldingInvite {

    readonly invite: {
        readonly queryString: string
        readonly type: "holding"
        readonly id: string
        readonly email: string
        readonly holdingName: string
    }

}

export interface IOmniaService {

    readonly init: boolean
    readonly ready: boolean
    readonly stateVersion: number
    readonly readOnly: boolean
    readonly permissions: UserPermissions

    readonly holdingId: number
    readonly holding: IHolding,

    readonly yearId: number
    readonly cropYear: CropYear

    refresh(): ng.IPromise<any>
    ensureInit(): ng.IPromise<any>

    getSession(): IOmniaSession

    getSettings(namespace: string): IOmniaSettings

}

export interface IOmniaSettingsKey<T = any> {
    readonly key: string;
    get(defaultValue?: T): T | undefined;
    set(value: T, defaultValue?: T);
}

export interface IOmniaSettings {
    getSettings(namespace: string): IOmniaSettings;
    getKey<T = any>(key: string): IOmniaSettingsKey<T>;
    get(key: string, defaultValue?: any);
    set(key: string, value: any, defaultValue?: any);
    remove(key: string);
}

export interface IOmniaCommand {

    text?: string,
    icon?: string,
    cssClass?: string,

    execute?: (dataContext?: any) => void,
    canExecute?: (dataContext?: any) => boolean,

    stop?: () => void,

    popupTemplate?: string,
    autoOpen?: boolean,

    datacontext?: any,

    foreground?: boolean

    isActive?: () => boolean

}

export interface IOmniaCommandGroup {

    readonly text: string
    readonly datacontext?: any
    readonly commands: IOmniaCommand[]
    readonly foreground: boolean

    foregroundCommands(): IOmniaCommand[]
    backgroundCommands(): IOmniaCommand[]
}

export interface IUibModalScope extends ng.IScope {

    $close(reason?: any): void
    $dismiss(result?: any): void

}

export interface ITab {
    key: string
    text: string
}

export interface ExternalModuleContext {

    readonly http: IOmniaHttpService
    readonly session: IOmniaSession
    readonly fields: SharedFieldsService
    readonly topo: IMapTopologyService

    createFieldsMap(options?: IFieldsMapContainerOptions): IFieldsMap
    createDataLayersMap(options?: IFieldsMapContainerOptions): IDatalayersMap

    apply(): void
    navigate(path: string): void
    getRouteParams(): { [key: string]: any }

    getService<T>(name: string): T

    disableShell(disable: boolean): () => void
    blockUi(block: boolean | string): void

}
